// react
import React from 'react';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AirRoundedIcon from '@mui/icons-material/AirRounded';
// Icons
import AppRegistrationRoundedIcon from '@mui/icons-material/AppRegistrationRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import DeviceHubRoundedIcon from '@mui/icons-material/DeviceHubRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import LanRoundedIcon from '@mui/icons-material/LanRounded';
import LightRoundedIcon from '@mui/icons-material/LightRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TroubleshootRoundedIcon from '@mui/icons-material/TroubleshootRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';

// shared components
import {
  DaluxIcon,
  DatahubIcon,
  EforsyningIcon,
} from '@/components/ui/icon/ThirdPartyIcons';

// relative
import type { NavSectionConfig } from './navigationTypes';

// Icon mapping
export const ICON_MAP: Record<string, React.ReactNode> = {
  dashboard: <DashboardRoundedIcon />,
  alarms: <NotificationsRoundedIcon />,
  devices: <DevicesRoundedIcon />,
  chart: <BarChartRoundedIcon />,
  settings: <SettingsRoundedIcon />,
  folder: <FolderRoundedIcon />,
  trending: <TrendingUpRoundedIcon />,
  build: <BuildRoundedIcon />,
  deepdive: <LanRoundedIcon />,
  portfolio: <CategoryRoundedIcon />,
  appRegistration: <AppRegistrationRoundedIcon />,
  troubleshoot: <TroubleshootRoundedIcon />,
  homeWork: <HomeWorkRoundedIcon />,
  deviceHub: <DeviceHubRoundedIcon />,
  router: <RouterRoundedIcon />,
  speed: <SpeedRoundedIcon />,
  light: <LightRoundedIcon />,
  energysavings: <EnergySavingsLeafRoundedIcon />,
  schedule: <ScheduleRoundedIcon />,
  energyxplorer: <TravelExploreRoundedIcon />,
  power: <BoltRoundedIcon color="power" />,
  water: <WaterDropRoundedIcon color="water" />,
  heat: <LocalFireDepartmentRoundedIcon color="heat" />,
  air: <AirRoundedIcon />,
  meters: <PlaceRoundedIcon />,
  data: <TimelineRoundedIcon />,
  file: <FileDownloadRoundedIcon />,
  help: <HelpOutlineRoundedIcon />,
  qos: <MonitorHeartOutlinedIcon />,
  add: <AddRoundedIcon />,
  'datahub-icon': <DatahubIcon />,
  'eforsyning-icon': <EforsyningIcon />,
  'dalux-icon': <DaluxIcon />,
};

// Static navigation configuration (no React elements in data - prevents re-memoization)
export const NAV_CONFIG: NavSectionConfig[] = [
  {
    id: 'operations',
    titleKey: 'navigation.sections.operations',
    visible: (ctx) => Boolean(ctx.isAdmin === true && ctx.adminViewMode), // Admin-only section
    items: [
      {
        id: 'services',
        labelKey: 'navigation.items.service-manager',
        iconName: 'appRegistration',
        path: '/$accountId/service-manager',
      },
      {
        id: 'deepdive',
        labelKey: 'navigation.items.deepdive',
        iconName: 'deepdive',
        path: '/$accountId/operations/deep-dive',
      },
      {
        id: 'portfolio',
        labelKey: 'navigation.items.portfolio-analysis',
        iconName: 'portfolio',
        path: '/$accountId/operations/portfolio-analysis',
      },
      {
        id: 'qos',
        labelKey: 'navigation.items.quality-of-service',
        iconName: 'qos',
        path: '/$accountId/operations/quality-of-service',
      },
      {
        id: 'pvo',
        labelKey: 'navigation.items.pvo',
        iconName: 'troubleshoot',
        path: '/$accountId/operations/predictive-heat-control/groups',
      },
      {
        id: 'pvo-internal',
        labelKey: 'navigation.items.pvo-internal',
        iconName: 'homeWork',
        path: '/$accountId/operations/predictive-heat-control',
      },
    ],
  },
  {
    id: 'alarms',
    titleKey: 'navigation.sections.alarms',
    visible: (ctx) => Boolean(ctx.isAdmin === true && ctx.adminViewMode),
    items: [
      {
        id: 'alarms',
        labelKey: 'navigation.items.alarms',
        iconName: 'alarms',
        path: '/$accountId/alarms',
      },
    ],
  },
  {
    id: 'predictive-heat-control',
    titleKey: 'navigation.sections.predictive-heat-control',
    visible: (ctx) =>
      Boolean(ctx.adminViewMode || ctx.services?.includes('PVO')),
    items: [
      {
        id: 'savings',
        labelKey: 'navigation.items.energysavings',
        iconName: 'energysavings',
        path: '/$accountId/predictive-heat-control',
      },
    ],
  },
  {
    id: 'energyxplorer',
    titleKey: 'navigation.sections.energyxplorer',
    visible: (ctx) =>
      Boolean(
        ctx.adminViewMode ||
        ctx.services?.includes('PortfolioAnalysis') ||
        ctx.services?.includes('EnergyXplorer'),
      ),
    items: [
      {
        id: 'portfolio-section',
        labelKey: 'navigation.items.portfolio',
        iconName: 'homeWork',
        path: '/$accountId/energyxplorer/portfolio',
        visible: (ctx) =>
          Boolean(
            ctx.adminViewMode ||
            ctx.services?.includes('PortfolioAnalysis') ||
            ctx.services?.includes('EnergyXplorer'),
          ),
        children: [
          // {
          //   id: 'portfolio-dashboard',
          //   labelKey: 'navigation.items.dashboard',
          //   iconName: 'dashboard',
          //   path: '/$accountId/energyxplorer/portfolio/dashboard',
          //   visible: (ctx) =>
          //     Boolean(
          //       ctx.adminViewMode ||
          //       ctx.services?.includes('PortfolioAnalysis') ||
          //       ctx.services?.includes('EnergyXplorer'),
          //     ),
          // },
          {
            id: 'portfolio',
            labelKey: 'navigation.items.portfolio-overview',
            iconName: 'homeWork',
            path: '/$accountId/energyxplorer/portfolio/overview',
            visible: (ctx) =>
              Boolean(
                ctx.adminViewMode ||
                ctx.services?.includes('PortfolioAnalysis'),
              ),
          },
          {
            id: 'power',
            labelKey: 'navigation.items.power',
            iconName: 'power',
            path: '/$accountId/energyxplorer/portfolio/power',
            visible: (ctx) =>
              Boolean(
                ctx.adminViewMode ||
                ctx.services?.includes('PortfolioAnalysis'),
              ),
          },
          {
            id: 'water',
            labelKey: 'navigation.items.water',
            iconName: 'water',
            path: '/$accountId/energyxplorer/portfolio/water',
            visible: (ctx) =>
              Boolean(
                ctx.adminViewMode ||
                ctx.services?.includes('PortfolioAnalysis'),
              ),
          },
          {
            id: 'heat',
            labelKey: 'navigation.items.heat',
            iconName: 'heat',
            path: '/$accountId/energyxplorer/portfolio/heat',
            visible: (ctx) =>
              Boolean(
                ctx.adminViewMode ||
                ctx.services?.includes('PortfolioAnalysis'),
              ),
          },
          {
            id: 'add_matrikel',
            labelKey: 'navigation.items.add_matrikel',
            iconName: 'add',
            path: '/$accountId/energyxplorer/portfolio/site/create',
            visible: (ctx) =>
              Boolean(
                ctx.adminViewMode ||
                ctx.services?.includes('PortfolioAnalysis'),
              ),
          },
        ],
      },
      {
        id: 'deepdive',
        labelKey: 'navigation.items.deepdive',
        iconName: 'deepdive',
        path: '/$accountId/energyxplorer/deepdive',
        visible: (ctx) =>
          Boolean(
            ctx.adminViewMode ||
            ctx.services?.includes('EnergyXplorer') ||
            ctx.services?.includes('PortfolioAnalysis'),
          ),
        children: [],
      },
    ],
  },
  {
    id: 'analytics',
    titleKey: 'navigation.sections.analytics',
    visible: (ctx) =>
      Boolean(ctx.adminViewMode || ctx.services?.includes('Analytics')),
    items: [
      {
        id: 'data-viewer',
        labelKey: 'navigation.items.data-viewer',
        iconName: 'data',
        path: '/$accountId/view-data',
      },
      {
        id: 'export-data',
        labelKey: 'navigation.items.export-data',
        iconName: 'file',
        path: '/$accountId/export-data',
      },
    ],
  },
  {
    id: 'dashboards',
    titleKey: 'navigation.sections.dashboards',
    visible: (ctx) =>
      Boolean(
        ctx.adminViewMode ||
        ctx.services?.includes('DashboardPowerBasic') ||
        ctx.services?.includes('DashboardHeatBasic') ||
        ctx.services?.includes('DashboardWaterBasic') ||
        ctx.services?.includes('DashboardCompressedAir'),
      ),
    items: [
      {
        id: 'power-basic-dashboard',
        labelKey: 'navigation.items.power-basic',
        iconName: 'power',
        path: '/$accountId/dashboards/power-basic',
        visible: (ctx) =>
          Boolean(
            ctx.adminViewMode || ctx.services?.includes('DashboardPowerBasic'),
          ),
      },
      {
        id: 'heat-basic-dashboard',
        labelKey: 'navigation.items.heat-basic',
        iconName: 'heat',
        path: '/$accountId/dashboards/heat-basic',
        visible: (ctx) =>
          Boolean(
            ctx.adminViewMode || ctx.services?.includes('DashboardHeatBasic'),
          ),
      },
      {
        id: 'water-basic-dashboard',
        labelKey: 'navigation.items.water-basic',
        iconName: 'water',
        path: '/$accountId/dashboards/water-basic',
        visible: (ctx) =>
          Boolean(
            ctx.adminViewMode || ctx.services?.includes('DashboardWaterBasic'),
          ),
      },
      {
        id: 'compressed-air-basic-dashboard',
        labelKey: 'navigation.items.compressed-air-basic',
        iconName: 'air',
        path: '/$accountId/dashboards/compressed-air',
        visible: (ctx) =>
          Boolean(
            ctx.adminViewMode ||
            ctx.services?.includes('DashboardCompressedAir'),
          ),
      },
    ],
  },
  {
    id: 'monitor',
    titleKey: 'navigation.sections.legacy-dashboards',
    // visible: (ctx) =>
    //   ctx.adminViewMode || ctx.services?.includes('DashboardLegacy'),
    items: [],
  },
  {
    id: 'devices',
    titleKey: 'navigation.sections.devices',
    visible: (ctx) =>
      Boolean(ctx.adminViewMode || ctx.services?.includes('Devices')),
    items: [
      {
        id: 'devices',
        labelKey: 'navigation.items.devices',
        iconName: 'devices',
        path: '/$accountId/devices',
        visible: (ctx) => Boolean(ctx.isAdmin === true && ctx.adminViewMode), // Admin-only section
      },
      {
        id: 'gateways',
        labelKey: 'navigation.items.gateways',
        iconName: 'router',
        path: '/$accountId/gateways',
      },
      {
        id: 'sensors',
        labelKey: 'navigation.items.sensors',
        iconName: 'speed',
        path: '/$accountId/sensors',
      },
      {
        id: 'units',
        labelKey: 'navigation.items.units',
        iconName: 'light',
        path: '/$accountId/units',
      },
    ],
  },
  {
    id: 'weekschedule-manager',
    titleKey: 'navigation.sections.week-schedule-manager',
    visible: (ctx) =>
      Boolean(ctx.adminViewMode || ctx.services?.includes('Scheduler')),
    items: [
      {
        id: 'weekschedule',
        labelKey: 'navigation.items.week-scheduler',
        iconName: 'schedule',
        path: '/$accountId/weekschedule-manager',
      },
    ],
  },
  {
    id: 'integrations',
    titleKey: 'navigation.sections.integrations',
    visible: (ctx) =>
      Boolean(
        ctx.adminViewMode ||
        ctx.services?.includes('DatahubIntegration') ||
        ctx.services?.includes('EforsyningIntegration'),
      ),
    items: [
      {
        id: 'datahub-integration',
        labelKey: 'navigation.items.datahub-integration',
        iconName: 'datahub-icon',
        path: '/$accountId/datahub-integration',
        visible: (ctx) =>
          Boolean(
            ctx.adminViewMode || ctx.services?.includes('DatahubIntegration'),
          ),
      },
      {
        id: 'eforsyning-integration',
        labelKey: 'navigation.items.eforsyning-integration',
        iconName: 'eforsyning-icon',
        path: '/$accountId/eforsyning-integration',
        visible: (ctx) =>
          Boolean(
            ctx.adminViewMode ||
            ctx.services?.includes('EforsyningIntegration'),
          ),
      },
      {
        id: 'dalux-integration',
        labelKey: 'navigation.items.dalux-integration',
        iconName: 'dalux-icon',
        path: '/$accountId/dalux-integration',
        visible: (ctx) =>
          Boolean(
            ctx.adminViewMode || ctx.services?.includes('DaluxIntegration'),
          ),
      },
    ],
  },
  {
    id: 'support-faq-section',
    titleKey: 'navigation.sections.support-faq',
    items: [
      {
        id: 'support-faq',
        labelKey: 'navigation.items.support-faq',
        iconName: 'help',
        path: '/$accountId/support',
      },
    ],
  },
];
